import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { ClientesMigrados, ClientesMigradosPagingRes } from '@/shared/interfaces/erp';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchClientesMigradosAll = (params?: getClientesMigradosParams, enabled = true) => {
  return useQuery({
    queryKey: ['CLIENTES MIGRADOS', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getClientesMigradosAll(params);
    },
    enabled,
  });
};

export const useGetClientesMigrados = (id: string) => {
  return useQuery({
    queryKey: ['CLIENTES MIGRADOS', id],
    queryFn: async () => {
      return getClientesMigrados(id);
    },
  });
};

export const useCreateClientesMigrados = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createClientesMigrados,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['CLIENTES MIGRADOS'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Cliente migrado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateClientesMigrados = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateClientesMigrados,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['CLIENTES MIGRADOS'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Cliente migrado actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveClientesMigrados = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeClientesMigrados,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['CLIENTES MIGRADOS'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getClientesMigradosParams = Partial<ClientesMigrados> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
  identificacion?: string;
  razon_social?: string;
};

export type CreateClientesMigradosParams = Omit<ClientesMigrados, 'id_cliente_migrado'>;
export type UpdateClientesMigradosParams = {
  id: string;
  data: Partial<CreateClientesMigradosParams>;
};

export const getClientesMigradosAll = async (params?: getClientesMigradosParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<ClientesMigradosPagingRes>(`${urlBackend}api/v1/clientesmigrados/?${queryParams}`, true);
};

export const getClientesMigrados = async (id: string) => {
  try {
    return await get<ClientesMigrados>(`${urlBackend}api/v1/clientesmigrados/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createClientesMigrados = async (data: CreateClientesMigradosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ClientesMigrados>(`${urlBackend}api/v1/clientesmigrados/`, data, true);
};

export const updateClientesMigrados = async ({ id, data }: UpdateClientesMigradosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ClientesMigrados>(`${urlBackend}api/v1/clientesmigrados/${id}/`, data, true);
};

export const removeClientesMigrados = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<ClientesMigrados>(`${urlBackend}api/v1/clientesmigrados/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};



